import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Calendar,
  User,
  Phone,
  CheckCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Wallet,
  Copy,
  Check,
  Camera,
  X,
  Building2,
} from "lucide-react";
import { IdentityHub } from "./ui/IdentityHub";
import { GuestCreditBadge } from "./credits/GuestCreditBadge";
import { useGuestCredit } from "../hooks/useGuestCredit";
import { WaitlistJoinModal } from "./waitlist/WaitlistJoinModal";
import { supabase } from "../lib/supabase";
import { generateUUID } from "../lib/uuid";
import { format, addDays } from "date-fns";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import { Business, ServiceOffering, StaffAssignment } from "../types";
import {
  generateTimeSlots,
  generateDateSlots,
  DEFAULT_START_HOUR,
  DEFAULT_END_HOUR,
  computeServicesDuration,
  computeVisibleDuration,
  isTimedService,
  hasTimedService,
} from "../lib/slots";
import { sanitizeServiceType, normalizePhoneNumber } from "../lib/utils";
import { StaffAssignmentSchema, forensicValidate } from "../lib/schemas";
import { sendWhatsAppClient } from "../lib/whatsapp-meta";
import { getContrastTextColor, getSafeTextColor } from "../lib/color-utils";
import { Footer } from "./Footer";
import { useUI } from "../hooks/useUI";
import { UTC_OFFSET_MINUTES, HAJEZ_SUPPORT_WHATSAPP } from "../lib/constants";
import { isSlotBeyondClientReach } from "../lib/slots";
import { StatusScreen } from "./common/StatusScreen";
import { LoadingScreen } from "./common/LoadingScreen";
import { compressImage } from "../lib/image-utils";

export function BookingForm() {
  const { business } = useOutletContext<{ business: Business }>();
  const dateScrollRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [initError, setInitError] = useState(false);
  const isMounted = useRef(true);

  const idempotencyKeyRef = useRef<string>("");
  const [workers, setWorkers] = useState<StaffAssignment[]>([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);

  useEffect(() => {
    idempotencyKeyRef.current = generateUUID();
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const navigate = useNavigate();
  const { globalAlert, globalToast } = useUI();
  const [step, setStep] = useState(1);

  // Make the iPhone edge-swipe-back gesture (and the physical/Android back
  // button) step back through the wizard instead of leaving the booking page
  // entirely. We push one history entry per step beyond 1, so popping one
  // via a back gesture reads as "previous step"; only swiping back from step
  // 1, where no guard entry is left, actually exits the page.
  const wizardHistoryDepthRef = useRef(0);
  const ignorePopStatesRef = useRef(0);

  useEffect(() => {
    const desiredDepth = step - 1;
    const currentDepth = wizardHistoryDepthRef.current;

    if (desiredDepth > currentDepth) {
      for (let i = 0; i < desiredDepth - currentDepth; i++) {
        window.history.pushState({ hajezWizardStep: true }, "");
      }
      wizardHistoryDepthRef.current = desiredDepth;
    } else if (desiredDepth < currentDepth) {
      const stepsBack = currentDepth - desiredDepth;
      ignorePopStatesRef.current += stepsBack;
      window.history.go(-stepsBack);
      wizardHistoryDepthRef.current = desiredDepth;
    }
  }, [step]);

  useEffect(() => {
    const onPopState = () => {
      if (ignorePopStatesRef.current > 0) {
        ignorePopStatesRef.current -= 1;
        return;
      }
      wizardHistoryDepthRef.current = Math.max(
        0,
        wizardHistoryDepthRef.current - 1,
      );
      setStep((s) => Math.max(1, s - 1));
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const [showStep1Errors, setShowStep1Errors] = useState(false);
  // Spending the balance is opt-out, not opt-in: a client who has store credit
  // almost always wants it used, and making them hunt for a switch to pay less
  // is the wrong default. They can still turn it off to save it for next time.
  const [useCreditForDeposit, setUseCreditForDeposit] = useState<boolean>(true);
  const [waitlistModalOpen, setWaitlistModalOpen] = useState<boolean>(false);
  // Which specific unavailable time the client clicked "Waitlist" under.
  // Without this the modal had no idea which slot triggered it and always
  // defaulted to the full business-hours range, regardless of whether the
  // client clicked 09:15 or 16:45.
  const [waitlistTargetTime, setWaitlistTargetTime] = useState<string>("");
  const [copiedCliq, setCopiedCliq] = useState<boolean>(false);
  const [paymentProofFile, setPaymentProofFile] = useState<File | null>(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState<string | null>(
    null,
  );
  const [depositReference, setDepositReference] = useState<string>("");

  const handleProofFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Automatically optimize/compress phone photos and screenshots before setting state
      const optimizedFile = await compressImage(file);

      if (optimizedFile.size > 5 * 1024 * 1024) {
        globalAlert({
          title: "File Too Large",
          message: "Please upload an image smaller than 5 MB.",
          type: "warning",
        });
        return;
      }

      setPaymentProofFile(optimizedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentProofPreview(reader.result as string);
      };
      reader.readAsDataURL(optimizedFile);
    } catch (err) {
      console.error("Failed to compress payment proof image:", err);
      setPaymentProofFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentProofPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const serviceOfferings = useMemo(() => {
    const raw = business.service_offerings || [];
    return raw.filter((s) => s.show_on_storefront !== false);
  }, [business.service_offerings]);

  const groupedServices = useMemo(() => {
    const groups: Record<string, ServiceOffering[]> = {};
    serviceOfferings.forEach((service) => {
      const section = service.section?.trim() || "General";
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(service);
    });
    return groups;
  }, [serviceOfferings]);

  const [formData, setFormData] = useState({
    client_name: "",
    phone_number: "",
    selected_services: [] as ServiceOffering[],
    requested_date: format(new Date(), "yyyy-MM-dd"),
    requested_time: "",
  });

  // Owned here, not inside GuestCreditBadge -- see useGuestCredit for why.
  // A salon with store credits switched off has no balance to offer, and
  // get_guest_salon_credit already returns 0 in that case; skipping the call
  // entirely just avoids a pointless round trip.
  const { balance: rawGuestCredit, loading: loadingGuestCredit } =
    useGuestCredit(
      business.enable_salon_credits !== false ? business.id : undefined,
      formData.phone_number,
    );
  const guestCreditBalance =
    business.enable_salon_credits !== false ? rawGuestCredit : 0;

  const isStep1Valid = useMemo(() => {
    return (
      formData.client_name.trim().length > 0 &&
      normalizePhoneNumber(formData.phone_number) !== null
    );
  }, [formData.client_name, formData.phone_number]);

  // A basket of nothing but products is not an appointment -- it would still
  // have to reserve a stylist's slot for a transaction needing none of her
  // time. Products stay bookable, but only alongside a real service.
  const hasTimedSelection = useMemo(
    () => hasTimedService(formData.selected_services),
    [formData.selected_services],
  );

  const isStep2Valid = hasTimedSelection;

  const totalPrice = formData.selected_services.reduce(
    (sum, s) => sum + s.price,
    0,
  );

  // The deposit maths lived in two places that disagreed. This memo decided
  // whether Confirm was enabled using the credit-adjusted figure, while the
  // payment card rendered `netDue = requiredDeposit` -- credit not subtracted
  // at all. So a client whose balance covered the deposit was shown the full
  // amount and a receipt upload, allowed to press Confirm without either, and
  // then met with a generic failure: create_booking_secure raised
  // payment_proof_required because nothing had been sent. One source of truth
  // now, and the RPC actually spends the credit.
  const depositBreakdown = useMemo(() => {
    if (!business.require_upfront_payment) {
      return {
        requiredDeposit: 0,
        creditApplied: 0,
        netDue: 0,
        isFull: false,
      };
    }
    const isFull = business.upfront_payment_mode === "full_prepayment";
    const requiredDeposit = isFull
      ? totalPrice
      : Math.min(business.upfront_deposit_amount ?? 5, totalPrice);
    const creditApplied = useCreditForDeposit
      ? Math.min(guestCreditBalance, totalPrice)
      : 0;
    return {
      requiredDeposit,
      creditApplied,
      netDue: Math.max(0, requiredDeposit - creditApplied),
      isFull,
    };
  }, [
    business.require_upfront_payment,
    business.upfront_payment_mode,
    business.upfront_deposit_amount,
    totalPrice,
    guestCreditBalance,
    useCreditForDeposit,
  ]);

  const isStep3Valid = useMemo(() => {
    if (!business.require_upfront_payment) return true;
    if (depositBreakdown.netDue <= 0) return true;
    return Boolean(paymentProofFile) || depositReference.trim().length > 0;
  }, [
    business.require_upfront_payment,
    depositBreakdown.netDue,
    paymentProofFile,
    depositReference,
  ]);

  const hasStartingPrice = useMemo(() => {
    return formData.selected_services.some((s) => s.is_starting_price);
  }, [formData.selected_services]);

  const scrollDates = (direction: "left" | "right") => {
    if (!dateScrollRef.current) return;
    const scrollAmount = 300;
    dateScrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // Dynamic slots based on business schedule and selected date
  const totalDuration = useMemo(
    () => computeServicesDuration(formData.selected_services),
    [formData.selected_services],
  );

  // What the client is told, as opposed to `totalDuration` (buffer-included)
  // which is what the calendar actually reserves. Buffer is cleanup/overrun
  // padding for the stylist -- showing it to the client just makes every
  // appointment look longer than it is for no reason they'd understand.
  const visibleDuration = useMemo(
    () => computeVisibleDuration(formData.selected_services),
    [formData.selected_services],
  );

  // Dynamic slots based on business schedule and selected date
  const timeSlots = useMemo(() => {
    if (!formData.requested_date) return [];

    const [year, month, day] = formData.requested_date.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);
    const dayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
      localDate.getDay()
    ];

    if (business.schedule && business.schedule[dayName]) {
      const daySchedule = business.schedule[dayName];
      if (!daySchedule.isOpen) return [];
      return generateTimeSlots(
        daySchedule.start,
        daySchedule.end,
        totalDuration,
      );
    }

    // Default: use global fallback hours if no schedule is defined for this day
    return generateTimeSlots(
      DEFAULT_START_HOUR,
      DEFAULT_END_HOUR,
      totalDuration,
    );
  }, [formData.requested_date, business.schedule, totalDuration]);

  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  /**
   * The times could not be fetched. Distinct from "there are no times" -- one
   * is a connection problem the customer can retry, the other is a full day.
   */
  const [slotsLoadFailed, setSlotsLoadFailed] = useState(false);
  const [waitlistableSlots, setWaitlistableSlots] = useState<string[]>([]);
  const [activeCapacitySlots, setActiveCapacitySlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [closedDates, setClosedDates] = useState<string[]>([]);

  const dateSlots = useMemo(
    () =>
      generateDateSlots(
        14,
        business.schedule || undefined,
        closedDates,
        totalDuration,
      ),
    [business.schedule, closedDates, totalDuration],
  );

  // VANISH PATTERN: If the current requested_date is no longer in the list (e.g. shift ended)
  // OR has become closed (e.g. closed-dates fetch resolved after the default "today" was set),
  // automatically transition to the next available date.
  useEffect(() => {
    if (dateSlots.length > 0) {
      const currentSlot = dateSlots.find(
        (ds) => ds.dateStr === formData.requested_date,
      );
      if (!currentSlot || currentSlot.isClosed) {
        // Pick the first available (not closed) date, or just the first date if all are closed
        const firstAvailable =
          dateSlots.find((ds) => !ds.isClosed) || dateSlots[0];
        setFormData((prev) => ({
          ...prev,
          requested_date: firstAvailable.dateStr,
        }));
      }
    }
  }, [dateSlots, formData.requested_date]);

  const fetchClosedDates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("business_closed_dates")
        .select("*")
        .eq("business_id", business.id);

      if (error) throw error;

      if (data && isMounted.current) {
        setClosedDates(data.map((d) => d.closed_date));
      }
    } catch (err) {
      if (!isMounted.current) return;
      const error = err as { message?: string };
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("Load failed")
      )
        return;
      console.error("Critical Failure (Closed Dates):", err);
      setInitError(true);
    }
  }, [business.id]);

  useEffect(() => {
    fetchClosedDates();
  }, [fetchClosedDates]);

  const fetchActiveWorkers = useCallback(async () => {
    if (!isMounted.current) return;
    try {
      const { data, error } = await supabase
        .from("business_staff")
        .select("id, name, role, is_active")
        .eq("business_id", business.id)
        .eq("is_active", true);

      if (error) throw error;
      if (data && isMounted.current) {
        const validated = forensicValidate(
          StaffAssignmentSchema.array(),
          data,
          "Active Workers",
        );
        setWorkers(validated as StaffAssignment[]);
      }
    } catch (err) {
      console.error("Error fetching workers:", err);
    }
  }, [business.id]);

  useEffect(() => {
    fetchActiveWorkers();
  }, [fetchActiveWorkers]);

  const fetchAvailableSlots = useCallback(
    async (date: string, signal?: AbortSignal) => {
      setLoadingSlots(true);
      setSlotsLoadFailed(false);
      try {
        const query = supabase.rpc("get_slot_availability", {
          p_business_id: business.id,
          p_date: date,
          p_duration_minutes: totalDuration,
          p_worker_id: selectedWorkerId,
        });

        if (signal) {
          query.abortSignal(signal);
        }

        const { data, error } = await query;

        if (error) throw error;

        if (data && Array.isArray(data)) {
          const typedData = data as {
            slot_time: string;
            is_available: boolean;
            is_waitlistable: boolean;
            capacity?: number;
          }[];
          setAvailableSlots(
            typedData.filter((s) => s.is_available).map((s) => s.slot_time),
          );
          setWaitlistableSlots(
            typedData.filter((s) => s.is_waitlistable).map((s) => s.slot_time),
          );
          setActiveCapacitySlots(
            typedData
              .filter((s) => (s.capacity ?? 0) > 0)
              .map((s) => s.slot_time),
          );
        }
      } catch (err: unknown) {
        if (!isMounted.current) return;
        const error = err as { name?: string; message?: string; hint?: string };

        // A request we cancelled ourselves -- the customer changed date or
        // stylist before this one came back. Nothing happened, say nothing.
        if (
          error.name === "AbortError" ||
          error.message?.includes("AbortError") ||
          error.hint?.includes("manual cancellation")
        ) {
          return;
        }

        // A dropped connection is NOT a cancellation, and it used to be
        // treated as one: the list stayed empty and the customer was told
        // "No Slots Available -- All Slots Taken". On salon wifi or patchy
        // mobile data that turns a momentary blip into a lost booking, and
        // the salon never finds out it happened.
        if (
          error.message?.includes("Failed to fetch") ||
          error.message?.includes("Load failed")
        ) {
          setSlotsLoadFailed(true);
          return;
        }

        console.error("Critical Failure (Available Slots):", err);
        setInitError(true);
      } finally {
        if (isMounted.current) {
          setLoadingSlots(false);
        }
      }
    },
    [business.id, totalDuration, selectedWorkerId],
  );

  useEffect(() => {
    if (formData.requested_date) {
      const controller = new AbortController();
      setAvailableSlots([]); // Clear stale data immediately to prevent phantom blocking
      setWaitlistableSlots([]);
      setActiveCapacitySlots([]);
      fetchAvailableSlots(formData.requested_date, controller.signal);
      setFormData((prev) => ({ ...prev, requested_time: "" }));

      return () => controller.abort();
    }
  }, [formData.requested_date, fetchAvailableSlots]);

  // Phase 22.4-C: Realtime Slot Hardening
  useEffect(() => {
    if (!business.id || !formData.requested_date) return;

    // Broadcast, not postgres_changes.
    //
    // postgres_changes enforces RLS, and public.bookings is readable only by
    // authenticated staff -- so a client on the storefront subscribed
    // successfully, reported SUBSCRIBED, and then received nothing, ever. The
    // slot they were looking at could be taken out from under them and the
    // button stayed enabled until they submitted and were rejected.
    //
    // Opening bookings to anon would have fixed the symptom by publishing
    // every client's name and phone number. Instead the database broadcasts a
    // content-free "this day changed" signal (see
    // 20260827120300_broadcast_slot_changes_to_storefront.sql) and we react by
    // re-running the same public availability query we already run.
    const channelName = `slots:${business.id}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "broadcast",
        { event: "slots_changed" },
        (message: { payload?: { requested_date?: string } }) => {
          // Ignore days the client is not looking at -- a busy salon changes
          // rows all day long and refetching for each one would put the grid
          // into a permanent loading state.
          const changed = message?.payload?.requested_date;
          if (changed && changed !== formData.requested_date) return;
          fetchAvailableSlots(formData.requested_date);
        },
      )
      .subscribe((status) => {
        // Expose status for E2E testing
        document.body.setAttribute("data-realtime-status", status);
      });

    return () => {
      document.body.removeAttribute("data-realtime-status");
      supabase.removeChannel(channel);
    };
  }, [business.id, formData.requested_date, fetchAvailableSlots]);

  // Side Effect: Auto-Deselect on Collision
  // If the currently selected time becomes unavailable (e.g. service duration change), clear it.
  useEffect(() => {
    if (!formData.requested_time) return;

    const isAvailable = availableSlots.includes(formData.requested_time);

    if (!isAvailable) {
      setFormData((prev) => ({ ...prev, requested_time: "" }));
    }
  }, [formData.requested_time, availableSlots]);

  // Phase 24.3-A: Decouple requested_time from selected_services
  // If ANY service change occurs (including same-duration swaps), reset time selection.
  // Dependency is selected_services (not totalDuration) to catch all toggles regardless of duration.
  useEffect(() => {
    setFormData((prev) => ({ ...prev, requested_time: "" }));
  }, [formData.selected_services]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const selectSlot = (time: string) => {
    if (availableSlots.includes(time)) {
      setFormData({ ...formData, requested_time: time });
    }
  };

  const toggleService = (service: ServiceOffering) => {
    setFormData((prev) => {
      const isSelected = prev.selected_services.some(
        (s) => s.name === service.name,
      );
      return {
        ...prev,
        selected_services: isSelected
          ? prev.selected_services.filter((s) => s.name !== service.name)
          : [...prev.selected_services, service],
      };
    });
  };

  const submitBooking = async () => {
    if (step < 3 || loading) return;

    if (!formData.requested_time) {
      globalAlert({
        title: "Pick a time",
        message: "Choose one of the available times above to continue.",
        type: "warning",
      });
      return;
    }

    if (!hasTimedService(formData.selected_services)) {
      globalAlert({
        title: "Choose a service",
        message:
          formData.selected_services.length > 0
            ? "Products are sold during a visit. Add a service so we know how long to book."
            : "Pick at least one service so we know how long to book.",
        type: "warning",
      });
      return;
    }

    const normalizedPhone = normalizePhoneNumber(formData.phone_number);
    if (!normalizedPhone) {
      globalAlert({
        title: "Check your number",
        message:
          "That doesn't look like a Jordanian WhatsApp number. It should start 077, 078 or 079 — for example 079 123 4567.",
        type: "error",
      });
      setStep(1);
      return;
    }

    setLoading(true);

    let paymentProofPath: string | null = null;
    if (paymentProofFile) {
      try {
        const fileExt = paymentProofFile.name.split(".").pop() || "jpg";
        const fileName = `${business.id}/${formData.requested_date}/${generateUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from("payment_proofs")
          .upload(fileName, paymentProofFile);

        if (uploadError) {
          console.error("Payment proof upload failed:", uploadError);
          throw new Error(
            `payment_proof_upload_failed: ${uploadError.message}`,
          );
        } else {
          paymentProofPath = fileName;
        }
      } catch (uploadErr) {
        console.error("Storage upload exception:", uploadErr);
        throw uploadErr;
      }
    }

    const serviceNamesForStorage = sanitizeServiceType(
      formData.selected_services,
      "en",
    );

    // Floored to one grid interval, same as the availability query above and
    // as `public.booking_occupied_minutes()` server-side. Writing a raw 0 here
    // (a service saved with no duration) produced a booking that occupied a
    // zero-width window, which no overlap check can see -- that is how a
    // stylist ended up with two clients in the same slot.
    const totalDuration = computeServicesDuration(formData.selected_services);

    const offsetHours = Math.floor(UTC_OFFSET_MINUTES / 60)
      .toString()
      .padStart(2, "0");
    const offsetMinutes = (UTC_OFFSET_MINUTES % 60).toString().padStart(2, "0");
    const requestedAt = `${formData.requested_date}T${formData.requested_time}:00+${offsetHours}:${offsetMinutes}`;

    try {
      const { data: bookingId, error: insertError } = await supabase.rpc(
        "create_booking_secure",
        {
          p_business_id: business.id,
          p_client_name: formData.client_name,
          p_phone_number: normalizedPhone,
          p_service_type: serviceNamesForStorage,
          p_services_booked: formData.selected_services,
          p_duration_minutes: totalDuration,
          p_total_price: totalPrice,
          p_requested_date: formData.requested_date,
          p_requested_time: formData.requested_time,
          p_requested_at: requestedAt,
          p_language: business.preferred_language || "en",
          p_worker_id: selectedWorkerId,
          p_idempotency_key: idempotencyKeyRef.current,
          p_payment_proof_path: paymentProofPath,
          p_deposit_reference: depositReference.trim() || null,
          p_credit_to_apply: depositBreakdown.creditApplied,
        },
      );

      if (insertError) throw insertError;

      let bookingIdString = "";
      let cancellationToken: string | undefined = undefined;
      let ticketState: string | undefined = undefined;

      if (typeof bookingId === "object" && bookingId !== null) {
        bookingIdString = String(
          (bookingId as { booking_id?: string }).booking_id || "",
        );
        cancellationToken = (bookingId as { cancellation_token?: string })
          .cancellation_token;
        ticketState = (bookingId as { ticket_state?: string }).ticket_state;
      } else {
        bookingIdString = String(bookingId || "");
      }

      setSuccess(true);

      // HAJ-85: don't tell the client "confirmed" while their deposit proof is still
      // pending staff review — no template exists yet for "we received your proof, hang
      // tight" (tracked separately), so we simply hold off sending anything here rather
      // than sending a message that overstates the booking's real state.
      if (business.whatsapp_enabled && ticketState !== "pending_verification") {
        sendWhatsAppClient({
          bookingId: bookingIdString,
          action: "confirm",
          phone: normalizedPhone,
          cancellationToken,
        }).catch((err) =>
          console.error("Background WhatsApp send failed:", err),
        );
      }
    } catch (err) {
      const errorObj = err as
        | { code?: string; message?: string; hint?: string }
        | Error;
      console.error("Error submitting booking:", errorObj);

      const errorCode = (errorObj as { code?: string }).code;
      const errorMessage = (errorObj as { message?: string }).message ?? "";
      const errorHint = (errorObj as { hint?: string }).hint;

      // Rate limiting is NOT a slot problem and must be its own branch.
      // create_booking_secure allows 10 bookings per 15 min per phone; before
      // this check, hitting that limit fell through to the full-screen
      // "System Unreachable" screen, which tells the client the site is down
      // when in fact their booking simply needs a moment. Their selected time
      // is deliberately preserved here -- nothing is wrong with it.
      if (errorMessage.includes("rate_limit_exceeded")) {
        setStep(3);
        globalAlert({
          title: "One moment",
          message:
            "We've received a few booking attempts from this number just now. Please wait a couple of minutes and try again — your selected time is still here.",
          type: "warning",
        });
        return;
      }

      // DB trigger validation violations — expected business logic, not system failures.
      // Match on err.message (the RAISE EXCEPTION name), display err.hint (human-readable).
      const VALIDATION_ERRORS = [
        "temporal_buffer_violation",
        "overlap_detected",
        "business_closed_violation",
        "outside_hours_violation",
      ];

      const isValidationError =
        VALIDATION_ERRORS.some((v) => errorMessage.includes(v)) ||
        errorCode === "23505" ||
        errorMessage.includes("unique_business_booking_date_time");

      if (isValidationError) {
        // Autonomous Recovery: reset time, refresh grid, stay on step 3.
        setFormData((prev) => ({ ...prev, requested_time: "" }));
        setStep(3);
        if (formData.requested_date) {
          fetchAvailableSlots(formData.requested_date);
        }
        globalAlert({
          title: "That time was just taken",
          message:
            errorHint ||
            "Someone booked this slot moments before you. Please pick another time — the times shown are now up to date.",
          type: "warning",
        });
        return;
      }

      if (
        errorMessage.includes("payment_proof_required") ||
        errorMessage.includes("payment_proof_upload_failed")
      ) {
        setStep(3);
        globalAlert({
          title: "Deposit Receipt Required",
          message:
            "Please attach a clear photo or screenshot of your CliQ transfer receipt to confirm your appointment.",
          type: "error",
        });
        return;
      }

      // Only a genuine loss of connectivity earns the full-screen takeover.
      // Anything else (an unexpected server error, a bad payload) is recoverable
      // from where the client is standing, so it stays an in-place alert rather
      // than implying the whole site is down.
      const isConnectionFailure =
        errorMessage.includes("Failed to fetch") ||
        errorMessage.includes("Load failed") ||
        errorMessage.includes("NetworkError") ||
        (typeof navigator !== "undefined" && navigator.onLine === false);

      if (isConnectionFailure) {
        console.error("Connection failure during dispatch:", err);
        setInitError(true);
        return;
      }

      console.error("Unexpected error during dispatch:", err);
      setStep(3);
      globalAlert({
        title: "That didn't go through",
        message:
          "Something went wrong on our end while confirming your booking. Please try again — if it keeps happening, message the salon directly and they'll book you in.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const effectiveLeadMinutes = useMemo(() => {
    const configuredLead = business?.min_lead_minutes ?? 15;
    return business?.require_upfront_payment
      ? Math.max(configuredLead, 20)
      : configuredLead;
  }, [business?.min_lead_minutes, business?.require_upfront_payment]);

  const categorizedSlots = useMemo(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    const checkAvailability = (time: string) => {
      return availableSlots.includes(time);
    };
    const checkWaitlistable = (time: string) => {
      return waitlistableSlots.includes(time);
    };
    const checkOffDuty = (time: string) => {
      // If capacity data is available for this date and time is not in activeCapacitySlots, it is off-duty
      return (
        activeCapacitySlots.length > 0 && !activeCapacitySlots.includes(time)
      );
    };
    // "Unreachable", not "past": a slot inside the lead notice window has not
    // started, but the server will not take a client booking for it either.
    const checkUnreachable = (time: string) => {
      return (
        formData.requested_date === today &&
        isSlotBeyondClientReach(
          time,
          formData.requested_date,
          UTC_OFFSET_MINUTES,
          Date.now(),
          effectiveLeadMinutes,
        )
      );
    };

    const mapSlot = (t: string) => ({
      time: t,
      isAvailable: checkAvailability(t),
      isWaitlistable: checkWaitlistable(t),
      isOffDuty: checkOffDuty(t),
      isUnreachable: checkUnreachable(t),
    });

    const morning = timeSlots
      .filter((t) => parseInt(t.split(":")[0]) < 12)
      .map(mapSlot);

    const afternoon = timeSlots
      .filter((t) => {
        const h = parseInt(t.split(":")[0]);
        return h >= 12 && h < 17;
      })
      .map(mapSlot);

    const evening = timeSlots
      .filter((t) => parseInt(t.split(":")[0]) >= 17)
      .map(mapSlot);

    return { morning, afternoon, evening };
  }, [
    timeSlots,
    availableSlots,
    waitlistableSlots,
    activeCapacitySlots,
    formData.requested_date,
    effectiveLeadMinutes,
  ]);

  const hasAvailableSlots =
    categorizedSlots.morning.length > 0 ||
    categorizedSlots.afternoon.length > 0 ||
    categorizedSlots.evening.length > 0;

  const isSelectedDateClosed = useMemo(() => {
    const currentSlot = dateSlots.find(
      (ds) => ds.dateStr === formData.requested_date,
    );
    return currentSlot?.isClosed ?? false;
  }, [dateSlots, formData.requested_date]);

  const primaryColor = business.primary_color || "#2563eb";

  const selectedServiceNames = formData.selected_services
    .map((s) => s.name)
    .join(", ");

  /** "Thursday, 27 August" -- spelled out, so there is nothing to decode. */
  const friendlyBookingDate = (isoDate: string) => {
    if (!isoDate) return "";
    try {
      return format(new Date(`${isoDate}T00:00:00`), "EEEE, d MMMM");
    } catch {
      return isoDate;
    }
  };

  if (success) {
    return (
      <StatusScreen
        variant="success"
        title="Success!"
        message="Booking Confirmed"
        description={
          <>
            Thank you,{" "}
            <span className="font-bold text-slate-900">
              {formData.client_name}
            </span>
            <br />
            Your booking at{" "}
            <span className="font-bold text-slate-900">{business.name}</span> is
            confirmed
            {/*
              The day and time belong on this screen.
              This used to end at "is confirmed" and say nothing about when --
              and when a salon has WhatsApp switched off, this screen is the
              only confirmation the client ever gets. They closed the tab
              knowing they had booked something, but not what.
            */}
            <br />
            <span className="mt-3 block font-bold text-slate-900">
              {friendlyBookingDate(formData.requested_date)} at{" "}
              {formData.requested_time?.slice(0, 5)}
            </span>
            <span className="block text-slate-500">
              {selectedServiceNames || "Your booking"}
            </span>
          </>
        }
        primaryColor={primaryColor}
        actions={[
          {
            label: "Manage Booking",
            onClick: () => {
              const normalized =
                normalizePhoneNumber(formData.phone_number) ||
                formData.phone_number;
              navigate(`/${business.slug}/manage?phone=${normalized}`);
            },
            variant: "primary",
          },
        ]}
      />
    );
  }

  if (initError) {
    const fallbackMessage = `Hi ${business.name}, I'm trying to book a ${visibleDuration} min appointment and the website is experiencing issues. Are you available?`;
    const waLink = `https://wa.me/${business.phone_number || HAJEZ_SUPPORT_WHATSAPP}?text=${encodeURIComponent(fallbackMessage)}`;

    return (
      <StatusScreen
        variant="error"
        title="No connection"
        message="We can't reach the booking page"
        description="Check your internet and try again. If it still won't load, you can message the salon on WhatsApp and they'll book you in directly."
        primaryColor={primaryColor}
        actions={[
          {
            label: "Retry Connection",
            onClick: () => {
              setInitError(false);
              fetchClosedDates();
              if (formData.requested_date)
                fetchAvailableSlots(formData.requested_date);
            },
            variant: "secondary",
          },
          {
            label: "Book via WhatsApp",
            onClick: () => window.open(waLink, "_blank"),
            variant: "primary",
          },
        ]}
      />
    );
  }

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col">
      <main className="flex-1 flex flex-col items-center justify-center p-4 lg:p-12 w-full">
        <div className="w-full max-w-2xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <IdentityHub business={business} primaryColor={primaryColor} />

          {/* Booking Form Card */}
          <div className="w-full bg-white rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-slate-50 relative overflow-hidden">
            <form
              onSubmit={(e) => {
                e.preventDefault();
              }}
              className="divide-y divide-slate-50"
            >
              {/* Step Indicator */}
              <div className="bg-slate-50/50 px-8 py-4 flex items-center justify-between border-b border-slate-100">
                <div className="flex items-center gap-2">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`h-1.5 rounded-full transition-all duration-300 ${s <= step ? "w-8" : "w-4 bg-slate-200"}`}
                      style={
                        s <= step
                          ? { backgroundColor: primaryColor }
                          : undefined
                      }
                    />
                  ))}
                </div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                  Step {step} / 3
                </span>
              </div>

              {/* Step 1: Personal Info */}
              {step === 1 && (
                <div className="p-8 space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                  <h2 className="text-lg font-sovereign-header text-slate-900 flex items-center gap-2 uppercase px-1">
                    <User className="text-slate-400" size={18} />
                    Your Information
                  </h2>

                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                        Full Name
                      </label>
                      <div className="relative group">
                        <User
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-slate-900 transition-colors"
                          size={18}
                        />
                        <input
                          type="text"
                          name="client_name"
                          required
                          value={formData.client_name}
                          onChange={handleChange}
                          placeholder="Hala Ahmad"
                          className="w-full pl-12 pr-4 py-5 bg-slate-50 border-none rounded-lg text-base focus:ring-2 focus:ring-slate-900/10 outline-none transition-all font-bold text-slate-900 placeholder:text-slate-300"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                        WhatsApp Number
                      </label>
                      <div className="relative group">
                        <Phone
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-slate-900 transition-colors"
                          size={18}
                        />
                        <input
                          type="tel"
                          name="phone_number"
                          required
                          value={formData.phone_number}
                          onChange={handleChange}
                          placeholder="079XXXXXXX"
                          className="w-full pl-12 pr-4 py-5 bg-slate-50 border-none rounded-lg text-base focus:ring-2 focus:ring-slate-900/10 outline-none transition-all font-bold text-slate-900 placeholder:text-slate-300"
                        />
                      </div>
                      {showStep1Errors &&
                        !normalizePhoneNumber(formData.phone_number) &&
                        formData.phone_number.length > 0 && (
                          <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest ml-1 animate-in fade-in slide-in-from-top-1">
                            Should start 077, 078 or 079
                          </p>
                        )}
                    </div>

                    {/* Step 1: Live Salon Credit Discovery. The lookup itself
                        runs in useGuestCredit at the top of this component --
                        keeping it here meant it was unmounted (and its debounce
                        cancelled) the instant the client pressed Continue. */}
                    {business.enable_salon_credits !== false && (
                      <GuestCreditBadge
                        balance={guestCreditBalance}
                        loading={loadingGuestCredit}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Services */}
              {step === 2 && (
                <div className="p-8 space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                  <h2 className="text-lg font-sovereign-header text-slate-900 flex items-center gap-2 uppercase px-1">
                    <div className="w-5 h-5 rounded-full bg-slate-900 flex items-center justify-center text-[10px] text-white font-bold">
                      2
                    </div>
                    Choose Services
                  </h2>

                  <div className="space-y-6">
                    {Object.entries(groupedServices).map(
                      ([section, services]) => (
                        <div key={section} className="space-y-3">
                          {Object.keys(groupedServices).length > 1 && (
                            <div className="flex items-center gap-4 px-1">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                {section}
                              </span>
                              <div className="h-px flex-1 bg-slate-100/50"></div>
                            </div>
                          )}
                          <div className="space-y-2">
                            {services.map((service) => {
                              const isSelected =
                                formData.selected_services.some(
                                  (s) => s.name === service.name,
                                );
                              // A product cannot be the whole booking.
                              const isLocked =
                                !isTimedService(service) &&
                                !hasTimedSelection &&
                                !isSelected;
                              return (
                                <button
                                  key={service.name}
                                  type="button"
                                  disabled={isLocked}
                                  onClick={() => toggleService(service)}
                                  className={`
                                w-full px-6 py-5 rounded-lg transition-all duration-200 flex items-center justify-between border-2 text-left
                                ${
                                  isLocked
                                    ? "bg-slate-50/60 border-transparent opacity-50 cursor-not-allowed"
                                    : isSelected
                                      ? "bg-white shadow-md"
                                      : "bg-slate-50 border-transparent hover:bg-slate-100"
                                }
                              `}
                                  style={
                                    isSelected
                                      ? {
                                          borderColor: primaryColor,
                                          backgroundColor: `${primaryColor}05`,
                                        }
                                      : undefined
                                  }
                                >
                                  <div className="flex items-center gap-4">
                                    <div
                                      className={`w-5 h-5 rounded-[4px] border-2 flex items-center justify-center transition-all duration-200 ${isSelected ? "border-transparent" : "border-slate-200"}`}
                                      style={
                                        isSelected
                                          ? {
                                              backgroundColor: primaryColor,
                                              borderColor: primaryColor,
                                            }
                                          : undefined
                                      }
                                    >
                                      {isSelected && (
                                        <CheckCircle
                                          size={14}
                                          className="text-white"
                                        />
                                      )}
                                    </div>
                                    <div className="flex flex-col items-start overflow-hidden">
                                      <span
                                        className={`font-bold uppercase tracking-tight text-sm leading-tight truncate w-full ${isSelected ? "text-slate-900" : "text-slate-500"}`}
                                      >
                                        {service.name}
                                      </span>
                                      {service.is_session_based &&
                                        service.sessions_count && (
                                          <span className="text-[10px] text-slate-400 font-bold leading-none mt-1">
                                            {service.sessions_count} sessions
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-1">
                                    {service.price > 0 && (
                                      <span
                                        className={`font-black text-sm ${isSelected ? "" : "text-slate-400"}`}
                                        style={
                                          isSelected
                                            ? {
                                                color:
                                                  getSafeTextColor(
                                                    primaryColor,
                                                  ),
                                              }
                                            : undefined
                                        }
                                      >
                                        {service.is_starting_price
                                          ? "From "
                                          : ""}
                                        {service.price} JOD
                                      </span>
                                    )}
                                    {!isTimedService(service) ? (
                                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter text-right">
                                        {isLocked
                                          ? "ADD A SERVICE FIRST"
                                          : "PRODUCT"}
                                      </span>
                                    ) : (
                                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                                        {service.duration_minutes ?? 30} MINS
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                  {formData.selected_services.length > 0 && (
                    <div className="flex items-center justify-between bg-slate-50 px-6 py-4 rounded-lg border border-slate-100 shadow-sm mt-4">
                      <div className="space-y-1">
                        <span className="block text-[10px] font-sovereign-header text-slate-400 uppercase tracking-widest leading-none">
                          Total Duration
                        </span>
                        {/* No "Product Purchase (0 MINS)" branch any more: a
                            products-only basket can no longer be booked, so
                            every basket that reaches here holds a real
                            appointment with a real length. Shows
                            visibleDuration, not totalDuration -- the client
                            was never meant to see the stylist's buffer time
                            baked into this number. */}
                        <span className="text-xs font-bold text-slate-600">
                          {visibleDuration} MINS
                        </span>
                      </div>
                      <div className="text-right space-y-1">
                        <span className="block text-[10px] font-sovereign-header text-slate-400 uppercase tracking-widest leading-none">
                          Estimated Total
                        </span>
                        <span
                          className="text-xl font-black"
                          style={{ color: getSafeTextColor(primaryColor) }}
                        >
                          {hasStartingPrice ? "From " : ""}
                          {totalPrice} JOD
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Schedule */}
              {step === 3 && (
                <div className="p-8 space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                  <h2 className="text-lg font-sovereign-header text-slate-900 flex items-center gap-2 uppercase px-1">
                    <Calendar className="text-slate-400" size={18} />
                    Date & Time Selection
                  </h2>

                  {/* Worker Selection Chips */}
                  {workers.length > 1 && (
                    <div className="space-y-3 px-1">
                      <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block leading-none">
                        Select Staff Member
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedWorkerId(null)}
                          className={`px-4 py-2.5 rounded-lg text-xs font-semibold tracking-wide transition-all border ${
                            selectedWorkerId === null
                              ? "bg-slate-900 border-slate-900 text-white shadow-md scale-[1.02]"
                              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300"
                          }`}
                        >
                          Any Available
                        </button>
                        {workers.map((w) => (
                          <button
                            key={w.id}
                            type="button"
                            onClick={() => setSelectedWorkerId(w.id)}
                            className={`px-4 py-2.5 rounded-lg text-xs font-semibold tracking-wide transition-all border ${
                              selectedWorkerId === w.id
                                ? "bg-slate-900 border-slate-900 text-white shadow-md scale-[1.02]"
                                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300"
                            }`}
                          >
                            {w.name || "Staff"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Date Slots */}
                  <div className="space-y-4">
                    <div className="relative group/scroller">
                      <button
                        type="button"
                        onClick={() => scrollDates("left")}
                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-10 h-10 bg-white shadow-xl rounded-lg flex items-center justify-center border border-slate-100 opacity-0 group-hover/scroller:opacity-100 transition-opacity hover:bg-slate-50 text-slate-600 hidden md:flex"
                      >
                        <ChevronLeft size={20} />
                      </button>

                      <div
                        ref={dateScrollRef}
                        className="flex gap-3 overflow-x-auto py-6 -mx-8 px-8 no-scrollbar"
                        style={{ scrollbarWidth: "none" }}
                      >
                        {dateSlots.map((ds) => {
                          const isSelected =
                            formData.requested_date === ds.dateStr;
                          const isClosed = ds.isClosed;
                          const isToday =
                            ds.dateStr === format(new Date(), "yyyy-MM-dd");
                          const isTomorrow =
                            ds.dateStr ===
                            format(addDays(new Date(), 1), "yyyy-MM-dd");

                          return (
                            <button
                              key={ds.dateStr}
                              type="button"
                              data-testid={
                                isToday
                                  ? "date-today"
                                  : isTomorrow
                                    ? "date-tomorrow"
                                    : "date-selector-btn"
                              }
                              data-date={ds.dateStr}
                              disabled={isClosed}
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  requested_date: ds.dateStr,
                                  requested_time: "",
                                })
                              }
                              className={`
                              flex-shrink-0 w-[5rem] py-6 rounded-lg text-center border-2 transition-all duration-300
                              ${
                                isClosed
                                  ? "bg-slate-50 text-slate-200 border-slate-100 cursor-not-allowed opacity-40 shadow-inner"
                                  : isSelected
                                    ? "scale-110 z-10"
                                    : "bg-white text-slate-700 border-slate-100 hover:border-slate-300 hover:bg-slate-50 active:scale-95"
                              }
                            `}
                              style={
                                isSelected && !isClosed
                                  ? {
                                      backgroundColor: primaryColor,
                                      color: getContrastTextColor(primaryColor),
                                      borderColor: primaryColor,
                                      boxShadow: `0 15px 30px ${primaryColor}40`,
                                    }
                                  : undefined
                              }
                            >
                              <div
                                className={`text-[9px] font-black uppercase tracking-widest mb-1 ${isSelected ? "" : "text-slate-400"}`}
                                style={
                                  isSelected ? { opacity: 0.8 } : undefined
                                }
                              >
                                {isToday ? "Today" : ds.dayName.substring(0, 3)}
                              </div>
                              <div className="text-2xl font-black mb-1 leading-none">
                                {ds.dayNum}
                              </div>
                              <div
                                className={`text-[9px] font-bold uppercase ${isSelected ? "" : "text-slate-300"}`}
                                style={
                                  isSelected ? { opacity: 0.8 } : undefined
                                }
                              >
                                {ds.monthName.substring(0, 3)}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={() => scrollDates("right")}
                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-10 h-10 bg-white shadow-xl rounded-lg flex items-center justify-center border border-slate-100 opacity-0 group-hover/scroller:opacity-100 transition-opacity hover:bg-gray-50 text-gray-600 hidden md:flex"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div className="space-y-6 text-center">
                    {loadingSlots ? (
                      <LoadingScreen
                        fullScreen={false}
                        color={primaryColor}
                        message="Checking Availability..."
                      />
                    ) : isSelectedDateClosed ? (
                      <StatusScreen
                        variant="empty"
                        title="Closed"
                        message="Business Closed On This Day"
                        description="Please select a different date to see available times."
                        fullScreen={false}
                        compact={true}
                        primaryColor={primaryColor}
                      />
                    ) : slotsLoadFailed ? (
                      <div
                        data-testid="slots-load-failed"
                        className="rounded-[12px] border border-amber-200 bg-amber-50 p-4 sm:p-5 text-center space-y-3"
                      >
                        <p className="text-sm font-bold text-amber-900">
                          We could not load the times
                        </p>
                        {/* Says the day is not full, because the empty list
                            that used to appear here said exactly that -- and a
                            customer who believes it closes the page. */}
                        <p className="text-xs text-amber-800 leading-relaxed">
                          This does not mean the day is full. Please check your
                          internet and try again.
                        </p>
                        <button
                          type="button"
                          data-testid="retry-slots"
                          onClick={() =>
                            fetchAvailableSlots(formData.requested_date)
                          }
                          className="inline-flex items-center justify-center h-10 px-5 rounded-[10px] text-white text-xs font-bold transition-transform active:scale-[0.98]"
                          style={{ backgroundColor: primaryColor }}
                        >
                          Try again
                        </button>
                      </div>
                    ) : !hasAvailableSlots ? (
                      <StatusScreen
                        variant="empty"
                        title="No Slots Available"
                        message="All Slots Taken"
                        description="Try selecting a different date or staff member to find available times."
                        fullScreen={false}
                        compact={true}
                        primaryColor={primaryColor}
                      />
                    ) : (
                      <div className="space-y-10">
                        {Object.entries(categorizedSlots).map(
                          ([category, slots]) =>
                            slots.length > 0 && (
                              <div key={category} className="space-y-5">
                                <div className="flex items-center gap-4">
                                  <span className="text-[9px] font-black text-slate-300 uppercase tracking-[0.3em]">
                                    {category}
                                  </span>
                                  <div className="h-px flex-1 bg-slate-100/50"></div>
                                </div>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                                  {slots.map(
                                    ({
                                      time,
                                      isAvailable,
                                      isWaitlistable,
                                      isOffDuty,
                                      isUnreachable,
                                    }) => {
                                      const isSelected =
                                        formData.requested_time === time;
                                      const waitlistEnabled =
                                        business.enable_waitlist !== false &&
                                        !isSelectedDateClosed;
                                      // A slot is only waitlistable if it is legitimately fully booked
                                      // by existing clients (capacity > 0 and bookings >= capacity),
                                      // not unreachable, and waitlist is enabled.
                                      const canWaitlist =
                                        isWaitlistable &&
                                        !isUnreachable &&
                                        waitlistEnabled;

                                      const isDisabled =
                                        isUnreachable ||
                                        isOffDuty ||
                                        (!isAvailable && !canWaitlist);

                                      // Priority: unreachable/offDuty > unavailable (waitlistable vs disabled) > selected > available
                                      const buttonStyles =
                                        isUnreachable || isOffDuty
                                          ? {
                                              className:
                                                "bg-slate-50 text-slate-200 border-slate-100 cursor-not-allowed opacity-40 grayscale",
                                              style: {},
                                            }
                                          : !isAvailable
                                            ? {
                                                className: canWaitlist
                                                  ? "bg-slate-50 text-slate-400 border-dashed border-slate-200 hover:border-slate-400 hover:bg-slate-100 cursor-pointer"
                                                  : "bg-slate-50 text-slate-200 border-slate-100 cursor-not-allowed opacity-40 grayscale",
                                                style: {},
                                              }
                                            : isSelected
                                              ? {
                                                  className:
                                                    "text-white border-transparent shadow-xl scale-105 z-10",
                                                  style: {
                                                    backgroundColor:
                                                      primaryColor,
                                                    color:
                                                      getContrastTextColor(
                                                        primaryColor,
                                                      ),
                                                    borderColor: primaryColor,
                                                  },
                                                }
                                              : {
                                                  className:
                                                    "bg-white text-slate-900 border-slate-100 hover:border-slate-900 hover:text-slate-900 active:scale-95",
                                                  style: {},
                                                };

                                      return (
                                        <button
                                          key={time}
                                          type="button"
                                          disabled={isDisabled}
                                          onClick={() => {
                                            if (isUnreachable || isOffDuty)
                                              return;
                                            if (!isAvailable) {
                                              if (canWaitlist) {
                                                setWaitlistTargetTime(time);
                                                setWaitlistModalOpen(true);
                                              }
                                              return;
                                            }
                                            selectSlot(time);
                                          }}
                                          className={`py-3 sm:py-3.5 px-1.5 sm:px-2 rounded-lg text-xs sm:text-sm font-black transition-all duration-200 border-2 uppercase tracking-wide flex flex-col items-center justify-center ${buttonStyles.className}`}
                                          style={buttonStyles.style}
                                        >
                                          <span>{time}</span>
                                          {canWaitlist && (
                                            <span className="text-[8px] sm:text-[9px] font-bold text-slate-400 uppercase tracking-tighter mt-0.5">
                                              Waitlist
                                            </span>
                                          )}
                                        </button>
                                      );
                                    },
                                  )}
                                </div>
                              </div>
                            ),
                        )}
                      </div>
                    )}

                    {formData.requested_time && (
                      <div className="space-y-4 mt-8 animate-in fade-in zoom-in duration-300">
                        {/* Booking Time Banner */}
                        <div
                          className="flex items-center justify-between p-6 rounded-lg border border-black/5 shadow-2xl text-left"
                          style={{
                            backgroundColor: primaryColor,
                            color: getContrastTextColor(primaryColor),
                          }}
                        >
                          <div className="flex items-center gap-5">
                            <div>
                              <p
                                className="text-[9px] font-bold uppercase tracking-[0.2em] mb-1"
                                style={{ opacity: 0.6 }}
                              >
                                Selected Time
                              </p>
                              <p className="text-sm font-bold tracking-tight uppercase">
                                {format(
                                  new Date(formData.requested_date),
                                  "EEEE, MMM do",
                                )}{" "}
                                at {formData.requested_time}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Jordan CliQ Upfront Payment & Credit Breakdown Card */}
                        {business.require_upfront_payment &&
                          (() => {
                            const {
                              isFull,
                              requiredDeposit,
                              creditApplied,
                              netDue,
                            } = depositBreakdown;
                            const cliqAlias =
                              business.cliq_alias?.trim() ||
                              business.phone_number ||
                              "";

                            const handleCopyCliq = () => {
                              if (!cliqAlias) return;
                              navigator.clipboard.writeText(cliqAlias);
                              setCopiedCliq(true);
                              globalToast({
                                message: "CliQ alias copied to clipboard!",
                                type: "success",
                              });
                              setTimeout(() => setCopiedCliq(false), 2500);
                            };

                            return (
                              <div
                                data-testid="cliq-payment-card"
                                className="bg-slate-50 border border-slate-200/90 rounded-[20px] p-4 sm:p-5 text-slate-900 text-left space-y-4 shadow-xs"
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                                  <div className="flex items-center gap-3">
                                    <div
                                      className="w-10 h-10 rounded-xl flex items-center justify-center font-bold shrink-0"
                                      style={{
                                        backgroundColor: `${primaryColor}15`,
                                        color: primaryColor,
                                      }}
                                    >
                                      <CreditCard size={18} />
                                    </div>
                                    <div>
                                      <h4 className="text-xs font-bold text-slate-900 tracking-tight">
                                        Advance Deposit Required
                                      </h4>
                                      <p className="text-[11px] text-slate-500 font-medium">
                                        {isFull
                                          ? "Full payment required to confirm appointment"
                                          : `${business.upfront_deposit_amount ?? 5.0} JOD deposit required to hold your slot`}
                                      </p>
                                    </div>
                                  </div>
                                  <span
                                    className="text-xs font-mono font-bold px-2.5 py-1 rounded-full border self-start sm:self-auto shrink-0"
                                    style={{
                                      backgroundColor: `${primaryColor}10`,
                                      borderColor: `${primaryColor}30`,
                                      color: primaryColor,
                                    }}
                                  >
                                    {requiredDeposit.toFixed(2)} JOD
                                  </span>
                                </div>

                                {/* Store balance: itemised, and switchable */}
                                {guestCreditBalance > 0 && (
                                  <div className="rounded-[12px] bg-slate-100 border border-slate-200 p-3 space-y-2.5">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        data-testid="use-store-credit-toggle"
                                        checked={useCreditForDeposit}
                                        onChange={(e) =>
                                          setUseCreditForDeposit(
                                            e.target.checked,
                                          )
                                        }
                                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-slate-900"
                                      />
                                      <span className="flex items-center gap-1.5 text-xs text-slate-700">
                                        <Wallet
                                          size={14}
                                          className="text-slate-600 shrink-0"
                                        />
                                        <span>
                                          Use my store balance (
                                          {guestCreditBalance.toFixed(2)} JOD
                                          available)
                                        </span>
                                      </span>
                                    </label>

                                    {creditApplied > 0 && (
                                      <div className="space-y-1 border-t border-slate-200 pt-2 text-xs">
                                        <div className="flex items-center justify-between text-slate-600">
                                          <span>
                                            {isFull
                                              ? "Total"
                                              : "Deposit required"}
                                          </span>
                                          <span className="font-mono">
                                            {requiredDeposit.toFixed(2)} JOD
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between font-semibold text-emerald-700">
                                          <span>Store balance applied</span>
                                          <span className="font-mono">
                                            -{creditApplied.toFixed(2)} JOD
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Net Transfer Requirement */}
                                {netDue > 0 ? (
                                  <div className="flex items-center justify-between pt-1">
                                    <span className="text-xs font-bold text-slate-600">
                                      Amount to Pay (CliQ):
                                    </span>
                                    <span
                                      data-testid="net-deposit-due"
                                      className="text-base font-mono font-black text-slate-900"
                                    >
                                      {netDue.toFixed(2)} JOD
                                    </span>
                                  </div>
                                ) : (
                                  <div
                                    data-testid="deposit-covered-by-credit"
                                    className="flex items-center gap-2 rounded-[12px] border border-emerald-200 bg-emerald-50 p-3 text-xs font-semibold text-emerald-800"
                                  >
                                    <Check
                                      size={14}
                                      className="shrink-0 text-emerald-600"
                                    />
                                    <span>
                                      {creditApplied >= totalPrice
                                        ? "Paid in full with your store balance. Nothing to transfer."
                                        : "Your store balance covers the deposit. Nothing to transfer now — the rest is due at the salon."}
                                    </span>
                                  </div>
                                )}

                                {/* CliQ Alias & Proof Section */}
                                {netDue > 0 && cliqAlias && (
                                  <div className="space-y-4 pt-3 border-t border-slate-200/80">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 bg-white p-3.5 rounded-[14px] border border-slate-200/80">
                                      <div className="min-w-0">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                          Salon CliQ Alias / Phone
                                        </p>
                                        <p className="text-sm font-mono font-black text-slate-900 tracking-wider mt-0.5 select-all break-all">
                                          {cliqAlias}
                                        </p>
                                        {business.cliq_bank_name?.trim() && (
                                          <div
                                            data-testid="cliq-bank-name-badge"
                                            className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-slate-100 text-xs font-semibold text-slate-600"
                                          >
                                            <Building2
                                              size={13}
                                              className="text-slate-400 shrink-0"
                                            />
                                            <span>
                                              Bank:{" "}
                                              <strong className="text-slate-900 font-bold">
                                                {business.cliq_bank_name.trim()}
                                              </strong>
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={handleCopyCliq}
                                        className="px-3.5 py-2 rounded-[10px] bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95 shadow-xs shrink-0 self-stretch sm:self-auto min-h-[40px]"
                                      >
                                        {copiedCliq ? (
                                          <>
                                            <Check
                                              size={14}
                                              className="text-emerald-400"
                                            />
                                            <span className="text-emerald-400">
                                              Copied
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <Copy size={14} />
                                            <span>Copy Alias</span>
                                          </>
                                        )}
                                      </button>
                                    </div>

                                    {/* Proof of Transfer Box */}
                                    <div className="space-y-2.5 pt-1">
                                      <label className="block text-xs font-bold text-slate-700">
                                        Upload Transfer Receipt / Screenshot{" "}
                                        <span className="text-rose-500">*</span>
                                      </label>

                                      {paymentProofPreview ? (
                                        <div className="relative rounded-xl border border-slate-200 overflow-hidden bg-white p-2.5 flex items-center gap-3">
                                          <img
                                            src={paymentProofPreview}
                                            alt="Transfer receipt"
                                            className="w-14 h-14 object-cover rounded-lg border border-slate-200 shrink-0"
                                          />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-bold text-slate-800 truncate">
                                              {paymentProofFile?.name ||
                                                "Receipt Image"}
                                            </p>
                                            <p className="text-[10px] text-emerald-600 font-bold flex items-center gap-1 mt-0.5">
                                              <Check size={11} /> Photo attached
                                            </p>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setPaymentProofFile(null);
                                              setPaymentProofPreview(null);
                                            }}
                                            className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-slate-100 transition shrink-0"
                                          >
                                            <X size={14} />
                                          </button>
                                        </div>
                                      ) : (
                                        <label
                                          data-testid="payment-proof-upload-label"
                                          className="border-2 border-dashed border-slate-200 hover:border-slate-400 rounded-xl p-4 sm:p-5 min-h-[96px] flex flex-col items-center justify-center gap-1.5 cursor-pointer bg-white hover:bg-slate-50 transition-all text-center"
                                        >
                                          <Camera
                                            size={20}
                                            className="text-slate-400"
                                          />
                                          <span className="text-xs font-bold text-slate-700">
                                            Tap to upload transfer photo /
                                            receipt
                                          </span>
                                          <span className="text-[10px] text-slate-400">
                                            Upload screenshot or camera photo
                                            (Max 5MB)
                                          </span>
                                          <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                                            data-testid="payment-proof-file-input"
                                            className="hidden"
                                            onChange={handleProofFileChange}
                                          />
                                        </label>
                                      )}

                                      {/* Secondary: Reference input */}
                                      <div className="pt-1">
                                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                                          Or type CliQ reference number / sender
                                          name
                                        </label>
                                        <input
                                          type="text"
                                          value={depositReference}
                                          onChange={(e) =>
                                            setDepositReference(e.target.value)
                                          }
                                          placeholder="e.g. CLIQ-89342 or Ahmad K."
                                          data-testid="deposit-reference-input"
                                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Wizard Actions */}
              <div className="p-8 bg-slate-50/50 flex items-center gap-4">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="flex-1 h-14 font-black rounded-lg border-2 border-slate-200 text-slate-400 hover:text-slate-900 hover:border-slate-900 hover:bg-white transition-all active:scale-[0.98] uppercase tracking-widest text-[10px] flex items-center justify-center gap-2"
                  >
                    <ChevronLeft size={16} />
                    Prev
                  </button>
                )}

                {step < 3 ? (
                  <button
                    key="continue-step-button"
                    type="button"
                    onClick={() => {
                      if (step === 1) {
                        if (!isStep1Valid) {
                          setShowStep1Errors(true);
                          globalAlert({
                            title: "We need a bit more",
                            message:
                              "Add your name and WhatsApp number so the salon can confirm your booking.",
                            type: "warning",
                          });
                          return;
                        }
                        setShowStep1Errors(false);
                      }
                      if (step === 2 && !isStep2Valid) {
                        globalAlert({
                          title: "Choose a service",
                          message:
                            "Pick at least one service so we know how long to book.",
                          type: "warning",
                        });
                        return;
                      }
                      setStep(step + 1);
                    }}
                    disabled={loading}
                    className={`flex-[2] h-14 font-black rounded-lg transition-all flex items-center justify-center gap-2 uppercase tracking-widest text-[10px] active:translate-y-0 ${
                      (step === 1 && !isStep1Valid) ||
                      (step === 2 && !isStep2Valid)
                        ? "opacity-40 cursor-not-allowed shadow-none"
                        : "shadow-xl hover:shadow-2xl hover:-translate-y-0.5"
                    }`}
                    style={{
                      backgroundColor: primaryColor,
                      color: getContrastTextColor(primaryColor),
                    }}
                  >
                    Continue
                    <ChevronRight size={16} />
                  </button>
                ) : (
                  <button
                    key="confirm-step-button"
                    type="button"
                    onClick={submitBooking}
                    disabled={
                      loading || !formData.requested_time || !isStep3Valid
                    }
                    className="flex-[2] h-14 font-black rounded-lg shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5 active:translate-y-0 uppercase tracking-widest text-[10px]"
                    style={{
                      backgroundColor: primaryColor,
                      color: getContrastTextColor(primaryColor),
                    }}
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <>
                        <span>Confirm Booking</span>
                        <CheckCircle size={16} />
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Manage My Bookings - Absolute Symmetrical Link (V221.9) */}
          <div className="flex justify-center">
            <Link
              to={`/${business.slug}/manage`}
              className="inline-flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-slate-900 transition-colors uppercase tracking-widest"
            >
              <ChevronLeft size={16} />
              Manage My Bookings
            </Link>
          </div>
          {/* Waitlist Join Modal Integration */}
          <WaitlistJoinModal
            isOpen={waitlistModalOpen}
            onClose={() => {
              setWaitlistModalOpen(false);
            }}
            onSuccess={() => {
              globalToast({
                message:
                  "You're on the list. We'll message you if this time frees up.",
                type: "success",
              });
            }}
            businessId={business.id}
            serviceType={sanitizeServiceType(formData.selected_services, "en")}
            targetDate={formData.requested_date}
            targetTime={waitlistTargetTime}
            primaryColor={primaryColor}
          />
        </div>
      </main>

      <Footer primaryColor={primaryColor} />
    </div>
  );
}
